import globalData from '@/lib/globals';
import { addFavoriteEstate } from '@/stores/favorites';
import { $authReady, $supabaseUser } from '@/stores/supabase';

interface JQueryStatic {
	( selector: Document ): {
		on(
			event: string,
			handler: ( event: unknown, formId: number | string ) => void
		): void;
	};
}

declare global {
	interface Window {
		jQuery?: JQueryStatic;
	}
}

const ERROR_CONFIRMATION_PATTERN =
	/konnte leider nicht|Systembenutzer nicht möglich/i;

function getEstateContactFormId(): number | null {
	const raw = (
		globalData as { config?: { estateContactFormId?: number | string } }
	 )?.config?.estateContactFormId;
	if ( raw === undefined || raw === null || raw === '' ) {
		return null;
	}
	const id = Number( raw );
	return Number.isFinite( id ) && id > 0 ? id : null;
}

function getEstateIdFromPage(): string | null {
	const gallery = document.querySelector(
		'[data-kam001-real-estate-blocks-component="EstateGallery"]'
	);
	if ( ! gallery ) {
		return null;
	}

	const propsAttr = gallery.getAttribute( 'data-props' );
	if ( ! propsAttr ) {
		return null;
	}

	try {
		const props = JSON.parse( propsAttr ) as { estateId?: string | number };
		if ( props.estateId === undefined || props.estateId === null ) {
			return null;
		}
		const id = String( props.estateId ).trim();
		return id || null;
	} catch {
		return null;
	}
}

function isSuccessfulConfirmation( formId: number ): boolean {
	const wrapper = document.getElementById(
		`gform_confirmation_wrapper_${ formId }`
	);
	if ( ! wrapper ) {
		return false;
	}
	const text = wrapper.textContent ?? '';
	return ! ERROR_CONFIRMATION_PATTERN.test( text );
}

function waitForAuthReady( timeoutMs = 2000 ): Promise< void > {
	if ( $authReady.get() ) {
		return Promise.resolve();
	}

	return new Promise( ( resolve ) => {
		const unsubscribeRef: { current?: () => void } = {};
		const timeout = window.setTimeout( () => {
			unsubscribeRef.current?.();
			resolve();
		}, timeoutMs );

		unsubscribeRef.current = $authReady.subscribe( ( ready ) => {
			if ( ready ) {
				window.clearTimeout( timeout );
				unsubscribeRef.current?.();
				resolve();
			}
		} );
	} );
}

async function handleInquiryConfirmation( formId: number ): Promise< void > {
	if ( ! isSuccessfulConfirmation( formId ) ) {
		return;
	}

	const estateId = getEstateIdFromPage();
	if ( ! estateId ) {
		return;
	}

	await waitForAuthReady();

	const userId = $supabaseUser.get()?.id ?? null;
	const added = await addFavoriteEstate( estateId, userId );

	if ( added && typeof window.__toast === 'function' ) {
		window.__toast( {
			title: 'Zur Merkliste hinzugefügt',
			description:
				'Das Objekt wurde nach Ihrer Anfrage in der Merkliste gespeichert.',
			status: 'success',
		} );
	}
}

function bindGformConfirmation( formId: number ): void {
	const $ = window.jQuery;
	if ( ! $ ) {
		return;
	}

	$( document ).on( 'gform_confirmation_loaded', ( _event, loadedFormId ) => {
		if ( Number( loadedFormId ) !== formId ) {
			return;
		}
		void handleInquiryConfirmation( formId );
	} );
}

export function initAddFavoriteOnInquiry(): void {
	const formId = getEstateContactFormId();
	if ( ! formId ) {
		return;
	}

	if ( window.jQuery ) {
		bindGformConfirmation( formId );
		return;
	}

	// Gravity Forms loads jQuery; retry briefly if our bundle runs first.
	let attempts = 0;
	const interval = window.setInterval( () => {
		attempts += 1;
		if ( window.jQuery ) {
			window.clearInterval( interval );
			bindGformConfirmation( formId );
			return;
		}
		if ( attempts >= 40 ) {
			window.clearInterval( interval );
		}
	}, 100 );
}
